import React, { useState } from "react";
import Paragraphs from "components/Paragraphs";
import EnumsList from "components/EnumsList";
import DescriptionList from "components/DescriptionList";
import { RiLinksLine } from "react-icons/ri";
import { COLORS } from "utils/styles";
import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";
import compact from "lodash/compact";
import classnames from "classnames";
import { useCleanAsPath } from "utils/hooks";
import { REQUIRED_TYPE, REQUIRED_TYPE_COPY } from "constants/docs";

export const getDefaultListItemRowClasses = () => {
  return {
    container: "w-100 bt hairline-1 pv3",
    content: "flex flex-column",
    row: "flex flex-wrap relative items-center",
    id: "f5 fw6 ma0 pa0",
    type: {
      value: "f6 fw5 black-60",
      description: "fw5 f6 black-60",
      required: "fw5 f7 red ttu"
    },
    paragraphs: "flex flex-column pt1",
    children: {
      container: "pb3",
      content: "ba hairline-1 br2"
    }
  };
};

const ListItemRowAnchor = ({ hover: externalHover, idLocation }) => {
  const [hover, setHover] = useState(false);

  const hoverTriggerProps = {
    href: `#${idLocation}`,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false)
  };

  const show = Boolean(externalHover || hover);

  return (
    <a {...hoverTriggerProps}>
      <div
        style={{
          position: "absolute",
          isolation: "isolate",
          transition: "transform .2s",
          opacity: show ? 1 : 0,
          left: -18,
          top: 2,
          paddingRight: 4,
          borderRadius: "50%"
        }}
      >
        <RiLinksLine size={14} color={COLORS.blue} />
      </div>
    </a>
  );
};

const ListItemRow = ({
  item,
  idPrefix = "",
  expanded,
  setExpanded,
  customClasses
}) => {
  const [hover, setHover] = useState(false);
  const cleanPath = useCleanAsPath();

  const classes = merge(getDefaultListItemRowClasses(), customClasses);
  const expandedItem = Boolean(expanded[item.id]);
  const isOpen = Boolean(item.highlighted) || expandedItem;
  const canToggle = Boolean(item.expandable);
  const toggleRow = canToggle
    ? () => {
        const updatedExpanded = cloneDeep(expanded);
        updatedExpanded[item.id] = !isOpen;

        setExpanded(updatedExpanded);
      }
    : () => {};

  const mergedIdPrefix = compact([idPrefix, item.id]).join(".");
  const idLocation = `${cleanPath}-${mergedIdPrefix}`;

  const itemProps = {
    className: classes.container,
    id: idLocation
  };

  const hoverTriggerProps = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false)
  };

  /**
   * Extend the list item with the id
   * Embed the link element
   */
  let additionalSpans = [];
  const hasRequiredType = Boolean(
    item.type &&
      item.type.required &&
      [REQUIRED_TYPE.ALWAYS, REQUIRED_TYPE.CONDITIONALLY].indexOf(
        item.type.required
      ) > -1
  );
  if (hasRequiredType) {
    additionalSpans.push(
      <span style={{ marginBottom: -2 }} className={classes.type.required}>
        {REQUIRED_TYPE_COPY[item.type.required]}
      </span>
    );
  }

  if (item.type && item.type.value) {
    additionalSpans.push(
      <span style={{ marginBottom: -1 }} className={classes.type.value}>
        {item.type.value}
      </span>
    );
  }

  if (item.type && item.type.description) {
    additionalSpans.push(
      <span style={{ marginBottom: -1 }} className={classes.type.description}>
        {item.type.description}
      </span>
    );
  }

  /**
   * If its optional add the "optional" notation
   */
  if (!hasRequiredType) {
    additionalSpans.push(
      <span style={{ marginBottom: -1 }} className={classes.type.description}>
        {REQUIRED_TYPE_COPY[REQUIRED_TYPE.OPTIONAL]}
      </span>
    );
  }

  return (
    <li {...itemProps}>
      <div className={classes.content}>
        <div
          className={classnames(classes.row, {
            pointer: canToggle
          })}
          onClick={toggleRow}
        >
          <ListItemRowAnchor hover={hover} idLocation={idLocation} />
          <div className="flex flex-row" {...hoverTriggerProps}>
            {idPrefix ? (
              <span className="black-40 fw5">{`${idPrefix}.`}</span>
            ) : null}
            <h4 className={classes.id}>{item.id}</h4>
          </div>
          {additionalSpans.map((node, nodeIx) => (
            <span key={nodeIx} className="pl1">
              {node}
            </span>
          ))}
        </div>
        {isOpen && (
          <div className={classes.paragraphs}>
            <Paragraphs paragraphs={item.paragraphs} />
            {item.enums && item.enums.list && item.enums.list.length > 0 && (
              <div className={`${classes.children.container} pt3`}>
                <div className={classes.children.content}>
                  <EnumsList {...item.enums} />
                </div>
              </div>
            )}
          </div>
        )}
        {item.children &&
          item.children.list &&
          item.children.list.length > 0 &&
          isOpen &&
          expanded && (
            <div className="pt3">
              <div className={classes.children.container}>
                <div className={classes.children.content}>
                  <DescriptionList
                    more={item.children.more}
                    list={item.children.list.map((itemChild) => ({
                      ...itemChild,
                      highlighted: false,
                      expandable: true
                    }))}
                    child={true}
                    condensed={true}
                    expanded={true}
                    idPrefix={mergedIdPrefix}
                    triggerFormatter={(expandState) =>
                      `${expandState ? "Show" : "Hide"} child attributes`
                    }
                    customClasses={{
                      container: "w-100 pb2",
                      notHighlighted: {
                        list: "list ma0 pa0",
                        more: {
                          container:
                            "flex flex-row justify-between pl2 pt2 pr2",
                          title: "f6 fw6",
                          action: "f6 fw6 pointer"
                        }
                      },
                      listItemRow: {
                        container: "w-100 bt hairline-1 pt2 mt2",
                        content: "flex flex-column ph2"
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
      </div>
    </li>
  );
};

export default ListItemRow;
